// ─────────────────────────────────────────────
// src/pages/DashboardPage.jsx
// Parent dashboard with three tabs:
//   📋 Enroll    — per-student school/session selection + pricing
//   📅 Schedule  — enrolled sessions with pay buttons
//   👤 My Account — edit parent + student info
//
// Sibling discount: 10% off for any student
// beyond the first enrolled by the same parent.
// Each student has its own independent school/session selection.
// ─────────────────────────────────────────────
import { useState, useEffect } from "react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { GlassCard, Badge, FormInput, FormSelect, Btn, GRADE_OPTIONS } from "../components/UI";
import { useSchools }   from "../hooks/useSchools";
import { useSessions } from "../hooks/useSessions";
import { supabase } from "../lib/supabase";
import { stripePromise } from "../lib/stripe";

function PaymentForm({ payingSessionData, payingEnrollment, students, payingPriceInfo, appliedPromo, parentInfo, schoolName, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  async function handlePay() {
    if (!stripe || !elements) return;
    setSaving(true);
    setErrorMsg("");

    const studentId = students[payingEnrollment.studentIdx]?.id;
    const { data, error: fnError } = await supabase.functions.invoke("create-payment-intent", {
      body: { sessionId: payingEnrollment.sessionId, studentId, promoCode: appliedPromo?.code ?? null },
    });

    if (fnError || !data?.clientSecret) {
      setErrorMsg("Could not initiate payment. Please try again.");
      setSaving(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret, {
      payment_method: { card: cardElement },
    });

    if (stripeError) {
      setErrorMsg(stripeError.message ?? "Payment failed. Please try again.");
      setSaving(false);
      return;
    }

    const { data: row, error: dbError } = await supabase
      .from("enrollments")
      .insert({
        student_id: studentId,
        session_id: payingEnrollment.sessionId,
        paid: true,
        paid_at: new Date().toISOString(),
        amount_paid: data.amount,
        stripe_payment_intent_id: paymentIntent.id,
        promo_code: appliedPromo?.code ?? null,
        sibling_discount: payingPriceInfo?.discount ?? false,
      })
      .select()
      .single();

    if (dbError) {
      setErrorMsg(`Payment succeeded but enrollment save failed. Contact support with payment ID: ${paymentIntent.id}`);
      setSaving(false);
      return;
    }

    // Fire confirmation email — don't await, don't block the UX
    supabase.functions.invoke("send-enrollment-confirmation", {
      body: {
        parentName:   parentInfo?.name,
        parentEmail:  parentInfo?.email,
        studentName:  students[payingEnrollment.studentIdx]?.name,
        sessionTitle: payingSessionData.title,
        sessionDays:  payingSessionData.days,
        sessionTime:  payingSessionData.time,
        sessionDates: payingSessionData.dates,
        schoolName:   schoolName,
        isFree:       false,
        amount:       data.amount,
      },
    });

    onSuccess(row);
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"0.85rem" }}>
      <div>
        <div style={{ fontSize:"0.78rem", fontWeight:700, color:"rgba(255,255,255,0.45)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>Card Details</div>
        <div style={{ background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.14)", borderRadius:10, padding:"12px 14px" }}>
          <CardElement options={{ style: { base: { color:"#fff", fontFamily:"'Nunito',sans-serif", fontSize:"15px", "::placeholder":{ color:"rgba(255,255,255,0.26)" }, iconColor:"#FFD93D" }, invalid:{ color:"#FF6B6B", iconColor:"#FF6B6B" } } }} />
        </div>
      </div>
      {errorMsg && (
        <div style={{ background:"rgba(255,107,107,0.1)", border:"1px solid rgba(255,107,107,0.3)", borderRadius:10, padding:"0.7rem 1rem", fontSize:"0.83rem", fontWeight:700, color:"#FF6B6B" }}>
          {errorMsg}
        </div>
      )}
      <div style={{ display:"flex", gap:"0.7rem", marginTop:"0.3rem" }}>
        <Btn onClick={onCancel} variant="ghost" style={{ flex:"0 0 auto" }} disabled={saving}>Cancel</Btn>
        <Btn onClick={handlePay} variant="success" style={{ flex:1 }} disabled={saving || !stripe}>
          {saving ? "Processing…" : `🔒 Pay $${payingPriceInfo.price}`}
        </Btn>
      </div>
    </div>
  );
}

function findSession(sessionId, allSessions, fallback = []) {
  return allSessions.find((s) => s.id === sessionId) ?? fallback.find((s) => s.id === sessionId) ?? null;
}

const enrollKey = (sId, sIdx) => `${sId}__${sIdx}`;

// ── Pricing: 1st enrolled student = full price, 2nd+ = 10% off (if the session allows it)
function getPrice(session, studentIdx, allEnrolled) {
  const basePrice = session?.price ?? 0;
  const eligible = session?.sibling_discount_eligible !== false;
  const alreadyEnrolledIdxs = [...new Set(allEnrolled.map((e) => e.studentIdx))];
  const isAdditional = eligible && alreadyEnrolledIdxs.length > 0 && !alreadyEnrolledIdxs.includes(studentIdx);
  return { price: isAdditional ? Math.round(basePrice * 0.9) : basePrice, discount: isAdditional };
}

// ── Cart pricing: ordered so the first unique student pays full price, siblings get 10% off
// (only for sessions where the admin has enabled the sibling discount)
function getCartItemPrices(cartEnrollments, paidEnrollments, allSessions) {
  const paidStudentIdxs = new Set(paidEnrollments.map((e) => e.studentIdx));
  const seenStudentIdxs = new Set(paidStudentIdxs);
  return cartEnrollments.map((enrollment) => {
    const session = allSessions.find((s) => s.id === enrollment.sessionId) ?? null;
    if (!session) return null;
    const eligible = session.sibling_discount_eligible !== false;
    const isAdditional = eligible && seenStudentIdxs.size > 0 && !seenStudentIdxs.has(enrollment.studentIdx);
    const price = session.price === 0 ? 0 : (isAdditional ? Math.round(session.price * 0.9) : session.price);
    seenStudentIdxs.add(enrollment.studentIdx);
    return { enrollment, session, priceInfo: { price, discount: isAdditional && session.price > 0 } };
  }).filter(Boolean);
}

// ── CartPaymentForm: processes multiple enrollments sequentially with one card entry
function CartPaymentForm({ cartItems, students, parentInfo, schools, appliedPromo, onAllSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  const subtotal = cartItems.reduce((sum, item) => sum + item.priceInfo.price, 0);
  const firstPaidPrice = cartItems.find((item) => item.session.price > 0)?.priceInfo.price ?? 0;
  const promoDiscount = appliedPromo ? Math.min(appliedPromo.discountAmount, firstPaidPrice) : 0;
  const totalAmount = Math.max(0, subtotal - promoDiscount);
  const hasPaidItems = cartItems.some((item) => item.session.price > 0);

  async function handleCheckout() {
    if (!stripe || !elements) return;
    setProcessing(true);
    setErrorMsg("");
    const cardElement = elements.getElement(CardElement);
    let savedPaymentMethodId = null;
    const completed = [];
    let promoApplied = false;

    for (let i = 0; i < cartItems.length; i++) {
      setCurrentIdx(i);
      const item = cartItems[i];
      const studentId = students[item.enrollment.studentIdx]?.id;

      if (item.session.price === 0) {
        const { data: row, error: dbError } = await supabase
          .from("enrollments")
          .insert({ student_id: studentId, session_id: item.enrollment.sessionId, paid: true, paid_at: new Date().toISOString(), amount_paid: 0 })
          .select().single();
        if (dbError) { setErrorMsg(`Enrollment save failed for ${item.session.title}. Contact support.`); setProcessing(false); return; }
        supabase.functions.invoke("send-enrollment-confirmation", { body: { parentName: parentInfo.name, parentEmail: parentInfo.email, studentName: students[item.enrollment.studentIdx]?.name, sessionTitle: item.session.title, sessionDays: item.session.days, sessionTime: item.session.time, sessionDates: item.session.dates, schoolName: schools.find((sc) => sc.id === item.session.school_id)?.name, isFree: true, amount: 0 } });
        completed.push({ key: item.enrollment.key, dbRow: row });
        continue;
      }

      // Only apply promo to the first paid item
      const promoCodeForItem = !promoApplied ? (appliedPromo?.code ?? null) : null;
      promoApplied = true;

      const { data, error: fnError } = await supabase.functions.invoke("create-payment-intent", {
        body: { sessionId: item.enrollment.sessionId, studentId, promoCode: promoCodeForItem },
      });
      if (fnError || !data?.clientSecret) { setErrorMsg(`Could not initiate payment for ${item.session.title}. Please try again.`); setProcessing(false); return; }

      // First item: confirm with card element, which saves the PM to the Stripe Customer
      // (via setup_future_usage on the PI). Subsequent items reuse the saved PM ID.
      let stripeError, paymentIntent;
      if (savedPaymentMethodId) {
        ({ error: stripeError, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret, { payment_method: savedPaymentMethodId }));
      } else {
        ({ error: stripeError, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret, { payment_method: { card: cardElement } }));
        if (!stripeError) savedPaymentMethodId = paymentIntent.payment_method;
      }
      if (stripeError) { setErrorMsg(stripeError.message ?? "Payment failed. Please try again."); setProcessing(false); return; }

      const { data: row, error: dbError } = await supabase
        .from("enrollments")
        .insert({ student_id: studentId, session_id: item.enrollment.sessionId, paid: true, paid_at: new Date().toISOString(), amount_paid: data.amount, stripe_payment_intent_id: paymentIntent.id, promo_code: appliedPromo?.code ?? null, sibling_discount: item.priceInfo.discount })
        .select().single();

      if (dbError) {
        // Recovery: enrollment may already exist (e.g. created by a webhook before this insert)
        const { data: existing } = await supabase
          .from("enrollments")
          .select("id, paid")
          .eq("student_id", studentId)
          .eq("session_id", item.enrollment.sessionId)
          .maybeSingle();
        if (existing) {
          completed.push({ key: item.enrollment.key, dbRow: existing });
          continue;
        }
        setErrorMsg(`Payment succeeded but enrollment save failed for ${item.session.title}. Please email info@marsacademy.ai with payment ID: ${paymentIntent.id}`);
        setProcessing(false);
        return;
      }

      supabase.functions.invoke("send-enrollment-confirmation", { body: { parentName: parentInfo.name, parentEmail: parentInfo.email, studentName: students[item.enrollment.studentIdx]?.name, sessionTitle: item.session.title, sessionDays: item.session.days, sessionTime: item.session.time, sessionDates: item.session.dates, schoolName: schools.find((sc) => sc.id === item.session.school_id)?.name, isFree: false, amount: data.amount } });
      completed.push({ key: item.enrollment.key, dbRow: row });
    }

    onAllSuccess(completed);
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"0.85rem" }}>
      <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:13, padding:"1rem" }}>
        {cartItems.map((item) => (
          <div key={item.enrollment.key} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8, gap:8 }}>
            <div style={{ minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:"0.83rem" }}>{item.session.title}</div>
              <div style={{ color:"rgba(255,255,255,0.4)", fontSize:"0.72rem", fontWeight:600 }}>
                {students[item.enrollment.studentIdx]?.name}
                {item.priceInfo.discount && <span style={{ color:"#22C55E" }}> · Sibling -10%</span>}
              </div>
            </div>
            <div style={{ textAlign:"right", flexShrink:0 }}>
              {item.priceInfo.discount && <div style={{ fontSize:"0.7rem", color:"rgba(255,255,255,0.3)", textDecoration:"line-through" }}>${item.session.price}</div>}
              <div style={{ fontWeight:800, fontSize:"0.85rem", color: item.priceInfo.price === 0 ? "#22C55E" : "#FFD93D" }}>
                {item.session.price === 0 ? "FREE" : `$${item.priceInfo.price}`}
              </div>
            </div>
          </div>
        ))}
        {promoDiscount > 0 && (
          <div style={{ display:"flex", justifyContent:"space-between", paddingTop:6, marginTop:2 }}>
            <span style={{ color:"#22C55E", fontSize:"0.8rem", fontWeight:700 }}>🎟️ Promo {appliedPromo.code}</span>
            <span style={{ color:"#22C55E", fontSize:"0.8rem", fontWeight:700 }}>-${promoDiscount}</span>
          </div>
        )}
        <div style={{ display:"flex", justifyContent:"space-between", borderTop:"1px solid rgba(255,255,255,0.1)", paddingTop:8, marginTop:4 }}>
          <span style={{ fontWeight:800 }}>Total</span>
          <span style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.4rem", color: totalAmount === 0 ? "#22C55E" : "#FFD93D" }}>
            {totalAmount === 0 ? "FREE" : `$${totalAmount}`}
          </span>
        </div>
      </div>

      {processing && (
        <div style={{ background:"rgba(155,92,246,0.1)", border:"1px solid rgba(155,92,246,0.3)", borderRadius:10, padding:"0.7rem 1rem", fontSize:"0.85rem", fontWeight:700, color:"#C084FC", textAlign:"center" }}>
          Processing {currentIdx + 1} of {cartItems.length}…
        </div>
      )}

      {hasPaidItems && totalAmount > 0 && (
        <div>
          <div style={{ fontSize:"0.78rem", fontWeight:700, color:"rgba(255,255,255,0.45)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>Card Details</div>
          <div style={{ background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.14)", borderRadius:10, padding:"12px 14px" }}>
            <CardElement options={{ style: { base: { color:"#fff", fontFamily:"'Nunito',sans-serif", fontSize:"15px", "::placeholder":{ color:"rgba(255,255,255,0.26)" }, iconColor:"#FFD93D" }, invalid:{ color:"#FF6B6B", iconColor:"#FF6B6B" } } }} />
          </div>
        </div>
      )}

      {errorMsg && (
        <div style={{ background:"rgba(255,107,107,0.1)", border:"1px solid rgba(255,107,107,0.3)", borderRadius:10, padding:"0.7rem 1rem", fontSize:"0.83rem", fontWeight:700, color:"#FF6B6B" }}>{errorMsg}</div>
      )}

      <div style={{ display:"flex", gap:"0.7rem", marginTop:"0.3rem" }}>
        <Btn onClick={onCancel} variant="ghost" style={{ flex:"0 0 auto" }} disabled={processing}>Cancel</Btn>
        <Btn onClick={handleCheckout} variant="success" style={{ flex:1 }} disabled={processing || !stripe}>
          {processing ? `Processing ${currentIdx + 1} of ${cartItems.length}…` : totalAmount === 0 ? "🚀 Confirm Enrollment" : `🔒 Pay $${totalAmount}`}
        </Btn>
      </div>
    </div>
  );
}

export default function DashboardPage({ user, setUser, setPage }) {
  const { schools }   = useSchools();
  const { sessions: allSessions, loading: sessionsLoading } = useSessions();
  const [students,        setStudents]        = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [selStudentIdx,   setSelStudentIdx]   = useState(0);

  // Per-student independent school/session state
  const [studentEnroll, setStudentEnroll] = useState([]);
  const [enrolled, setEnrolled] = useState([]);
  const [paid, setPaid] = useState({});
  // Session details for already-enrolled sessions (fetched without visibility filter)
  const [enrolledSessionDetails, setEnrolledSessionDetails] = useState([]);
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from("students")
      .select("*, enrollments(*)")
      .eq("parent_id", user.id)
      .order("created_at")
      .then(async ({ data }) => {
        const loaded = data ?? [];
        setStudents(loaded.map((s) => { const { enrollments: _, ...rest } = s; return rest; }));
        setStudentEnroll(loaded.map(() => ({ selSchool:"", selSession:"" })));

        // Rebuild enrolled + paid state from DB rows
        const restoredEnrolled = [];
        const restoredPaid = {};
        loaded.forEach((s, studentIdx) => {
          (s.enrollments ?? []).forEach((e) => {
            const key = enrollKey(e.session_id, studentIdx);
            restoredEnrolled.push({ sessionId: e.session_id, studentIdx, key, dbId: e.id });
            if (e.paid) restoredPaid[key] = true;
          });
        });
        setEnrolled(restoredEnrolled);
        setPaid(restoredPaid);

        // Fetch session details for enrolled sessions without the visibility filter
        // so paid enrollments always show on the Schedule tab even if a session is hidden
        const enrolledSessionIds = [...new Set(restoredEnrolled.map((e) => e.sessionId))];
        if (enrolledSessionIds.length > 0) {
          const { data: sessionData } = await supabase
            .from("sessions")
            .select("*")
            .in("id", enrolledSessionIds);
          setEnrolledSessionDetails(sessionData ?? []);
        }

        setStudentsLoading(false);
      });
  }, [user?.id]);

  // Payment modal state
  const [payStep, setPayStep] = useState(null);

  // Promo code state
  const [promoInput,        setPromoInput]        = useState("");
  const [appliedPromo,      setAppliedPromo]       = useState(null); // { id, code, discountAmount }
  const [promoChecking,     setPromoChecking]      = useState(false);
  const [promoError,        setPromoError]         = useState("");
  const [promoFreeEnrolling, setPromoFreeEnrolling] = useState(false);
  const [promoFreeError,     setPromoFreeError]     = useState("");

  // Free enrollment confirmation state
  const [freeConfirmKey, setFreeConfirmKey] = useState(null);
  const [freeConfirmSaving, setFreeConfirmSaving] = useState(false);
  const [freeConfirmError, setFreeConfirmError] = useState("");

  // Cart checkout state
  const [cartCheckoutOpen, setCartCheckoutOpen] = useState(false);
  const [cartPromoInput, setCartPromoInput] = useState("");
  const [cartAppliedPromo, setCartAppliedPromo] = useState(null);
  const [cartPromoChecking, setCartPromoChecking] = useState(false);
  const [cartPromoError, setCartPromoError] = useState("");

  // Dashboard tab
  const [activeTab, setActiveTab] = useState("enroll");

  // Edit modal — "parent" | number (student index) | null
  const [editModal, setEditModal] = useState(null);
  const [editData, setEditData] = useState({});

  // Add student modal
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({ name:"", age:"", grade:"", allergies:"", needs:"", tech_level:"", tech_notes:"" });

  // Parent editable info
  const [parentInfo, setParentInfo] = useState({ name: user?.name||"", email: user?.email||"", phone: user?.phone||"" });

  // ── Helpers ──────────────────────────────────
  const curEnroll  = studentEnroll[selStudentIdx] || { selSchool:"", selSession:"" };
  const sessions   = curEnroll.selSchool ? allSessions.filter((s) => s.school_id == curEnroll.selSchool) : [];
  const selSession = sessions.find((s) => s.id === curEnroll.selSession);
  const isEnrolled = (sId, sIdx) => enrolled.some((e) => e.sessionId === sId && e.studentIdx === sIdx);

  function setEnrollField(idx, field, val) {
    setStudentEnroll((prev) => prev.map((e, i) => (i === idx ? { ...e, [field]: val } : e)));
  }

  function doEnroll() {
    if (!curEnroll.selSession || isEnrolled(curEnroll.selSession, selStudentIdx)) return;
    if (selSession?.spots > 0 && (selSession?.enrolled_count ?? 0) >= selSession.spots) return;
    const key = enrollKey(curEnroll.selSession, selStudentIdx);
    setEnrolled((prev) => [...prev, { sessionId: curEnroll.selSession, studentIdx: selStudentIdx, key }]);
    if (selSession?.price === 0) {
      setFreeConfirmKey(key);
    }
    // Paid sessions go into the cart — parent clicks Checkout when ready
  }

  async function doFreeEnroll() {
    const enrollment = enrolled.find((e) => e.key === freeConfirmKey);
    if (!enrollment) return;
    setFreeConfirmSaving(true);
    setFreeConfirmError("");
    const studentId = students[enrollment.studentIdx]?.id;
    const { data: row, error } = await supabase
      .from("enrollments")
      .insert({
        student_id: studentId,
        session_id: enrollment.sessionId,
        paid: true,
        paid_at: new Date().toISOString(),
        amount_paid: 0,
      })
      .select()
      .single();
    setFreeConfirmSaving(false);
    if (error) {
      setFreeConfirmError("Could not complete enrollment. Please try again.");
      return;
    }
    setEnrolled((prev) => prev.map((e) => e.key === freeConfirmKey ? { ...e, dbId: row.id } : e));
    setPaid((prev) => ({ ...prev, [freeConfirmKey]: true }));

    // Fire confirmation email — don't await, don't block the UX
    supabase.functions.invoke("send-enrollment-confirmation", {
      body: {
        parentName:   parentInfo.name,
        parentEmail:  parentInfo.email,
        studentName:  students[enrollment.studentIdx]?.name,
        sessionTitle: freeConfirmSession.title,
        sessionDays:  freeConfirmSession.days,
        sessionTime:  freeConfirmSession.time,
        sessionDates: freeConfirmSession.dates,
        schoolName:   schools.find((sc) => sc.id === freeConfirmSession.school_id)?.name,
        isFree:       true,
        amount:       0,
      },
    });

    setFreeConfirmKey(null);
    setActiveTab("schedule");
  }

  async function applyPromoCode() {
    if (!promoInput.trim()) return;
    setPromoChecking(true);
    setPromoError("");
    const { data, error } = await supabase
      .from("promo_codes")
      .select("id, code, discount_amount, max_uses, current_uses, active")
      .eq("code", promoInput.trim().toUpperCase())
      .eq("active", true)
      .single();
    setPromoChecking(false);
    if (error || !data) { setPromoError("Invalid or expired promo code."); return; }
    if (data.max_uses !== null && data.current_uses >= data.max_uses) { setPromoError("This promo code has reached its usage limit."); return; }
    setAppliedPromo({ id: data.id, code: data.code, discountAmount: data.discount_amount });
    setPromoError("");
  }

  async function doPromoFreeEnroll() {
    const enrollment = enrolled.find((e) => e.key === payStep);
    if (!enrollment) return;
    setPromoFreeEnrolling(true);
    setPromoFreeError("");
    const studentId = students[enrollment.studentIdx]?.id;
    const { data: row, error } = await supabase
      .from("enrollments")
      .insert({
        student_id: studentId,
        session_id: enrollment.sessionId,
        paid: true,
        paid_at: new Date().toISOString(),
        amount_paid: 0,
        promo_code: appliedPromo?.code ?? null,
      })
      .select()
      .single();
    setPromoFreeEnrolling(false);
    if (error) { setPromoFreeError("Could not complete enrollment. Please try again."); return; }
    const sd = findSession(enrollment.sessionId, allSessions);
    supabase.functions.invoke("send-enrollment-confirmation", {
      body: {
        parentName:   parentInfo.name,
        parentEmail:  parentInfo.email,
        studentName:  students[enrollment.studentIdx]?.name,
        sessionTitle: sd?.title,
        sessionDays:  sd?.days,
        sessionTime:  sd?.time,
        sessionDates: sd?.dates,
        schoolName:   schools.find((sc) => sc.id === sd?.school_id)?.name,
        isFree:       true,
        amount:       0,
      },
    });
    // Increment promo uses for $0 enrollments (edge function not called in this path)
    if (appliedPromo) {
      const { data: promoRow } = await supabase
        .from("promo_codes")
        .select("current_uses")
        .eq("id", appliedPromo.id)
        .single();
      if (promoRow) {
        await supabase
          .from("promo_codes")
          .update({ current_uses: promoRow.current_uses + 1 })
          .eq("id", appliedPromo.id);
      }
    }

    setEnrolled((prev) => prev.map((e) => e.key === payStep ? { ...e, dbId: row.id } : e));
    setPaid((prev) => ({ ...prev, [payStep]: true }));
    setPayStep(null);
    setAppliedPromo(null);
    setPromoInput("");
    setPromoFreeError("");
    setActiveTab("schedule");
  }

  async function cartApplyPromo() {
    if (!cartPromoInput.trim()) return;
    setCartPromoChecking(true);
    setCartPromoError("");
    const { data, error } = await supabase
      .from("promo_codes")
      .select("id, code, discount_amount, max_uses, current_uses, active")
      .eq("code", cartPromoInput.trim().toUpperCase())
      .eq("active", true)
      .single();
    setCartPromoChecking(false);
    if (error || !data) { setCartPromoError("Invalid or expired promo code."); return; }
    if (data.max_uses !== null && data.current_uses >= data.max_uses) { setCartPromoError("This promo code has reached its usage limit."); return; }
    setCartAppliedPromo({ id: data.id, code: data.code, discountAmount: data.discount_amount });
    setCartPromoError("");
  }

  async function doAddStudent() {
    if (!newStudent.name) return;
    setSaving(true);
    const payload = {
      parent_id:  user.id,
      name:       newStudent.name,
      age:        newStudent.age ? Number(newStudent.age) : null,
      grade:      newStudent.grade      || null,
      allergies:  newStudent.allergies  || null,
      needs:      newStudent.needs      || null,
      tech_level: newStudent.tech_level || null,
      tech_notes: newStudent.tech_notes || null,
    };
    const { data, error } = await supabase
      .from("students")
      .insert(payload)
      .select()
      .single();
    setSaving(false);
    if (error) { console.error("add student:", error.message); return; }
    setStudents((prev) => [...prev, data]);
    setStudentEnroll((prev) => [...prev, { selSchool:"", selSession:"" }]);
    setNewStudent({ name:"", age:"", grade:"", allergies:"", needs:"", tech_level:"", tech_notes:"" });
    setShowAddStudent(false);
  }

  function openEdit(type) {
    setEditData(type === "parent" ? { ...parentInfo } : { ...students[type] });
    setEditModal(type);
  }

  async function saveEdit() {
    if (editModal === "parent") {
      setSaving(true);
      const { error } = await supabase
        .from("profiles")
        .update({
          name:  editData.name,
          email: editData.email,
          phone: editData.phone,
        })
        .eq("id", user.id);
      setSaving(false);
      if (error) { console.error("Error updating profile:", error.message); return; }
      setParentInfo(editData);
      setUser((prev) => ({ ...prev, ...editData }));
      setEditModal(null);
      return;
    }
    // Student edit — update in Supabase using the row's uuid
    setSaving(true);
    const studentId = students[editModal].id;
    const { error } = await supabase
      .from("students")
      .update({
        name:       editData.name,
        age:        editData.age ? Number(editData.age) : null,
        grade:      editData.grade      || null,
        allergies:  editData.allergies  || null,
        needs:      editData.needs      || null,
        tech_level: editData.tech_level || null,
        tech_notes: editData.tech_notes || null,
      })
      .eq("id", studentId);
    setSaving(false);
    if (error) { console.error("Error updating student:", error.message); return; }
    setStudents((prev) => prev.map((s, i) => (i === editModal ? { ...s, ...editData } : s)));
    setEditModal(null);
  }

  // Paying session info
  const payingEnrollment      = payStep ? enrolled.find((e) => e.key === payStep) : null;
  const payingSessionData     = payingEnrollment ? findSession(payingEnrollment.sessionId, allSessions) : null;
  const payingSiblingInfo     = payingSessionData && payingEnrollment
    ? getPrice(payingSessionData, payingEnrollment.studentIdx, enrolled.filter((e) => e.key !== payStep))
    : { price: 0, discount: false };
  const promoDiscountAmount   = appliedPromo ? Math.min(appliedPromo.discountAmount, payingSiblingInfo.price) : 0;
  const payingPriceInfo       = {
    ...payingSiblingInfo,
    promoDiscount: promoDiscountAmount,
    price: Math.max(0, payingSiblingInfo.price - promoDiscountAmount),
  };

  // Free confirmation info
  const freeConfirmEnrollment = freeConfirmKey ? enrolled.find((e) => e.key === freeConfirmKey) : null;
  const freeConfirmSession    = freeConfirmEnrollment ? findSession(freeConfirmEnrollment.sessionId, allSessions) : null;

  // Current selection price info
  const selPriceInfo = selSession
    ? getPrice(selSession, selStudentIdx, enrolled)
    : { price: 0, discount: false };

  // Cart: unpaid in-session items that haven't been saved to DB yet
  const cartEnrollments = enrolled.filter((e) => !e.dbId && !paid[e.key] && (findSession(e.sessionId, allSessions)?.price ?? 0) > 0);
  const paidEnrollments = enrolled.filter((e) => paid[e.key]);
  const cartItemsWithPrices = getCartItemPrices(cartEnrollments, paidEnrollments, allSessions);
  const cartTotal = cartItemsWithPrices.reduce((sum, item) => sum + item.priceInfo.price, 0);
  // Schedule: only items that have been saved to the database (confirmed signups)
  const scheduleEnrollments = enrolled.filter((e) => e.dbId);

  return (
    <div className="dash-panel" style={{ minHeight:"100vh", padding:"6rem 1.5rem 3rem" }}>
      <div style={{ maxWidth:1060, margin:"0 auto" }}>

        {/* ── Welcome Banner ── */}
        <div className="dash-welcome" style={{ background:"linear-gradient(135deg,rgba(255,107,107,0.12),rgba(155,92,246,0.12))", border:"1.5px solid rgba(255,255,255,0.1)", borderRadius:22, padding:"1.6rem 2rem", marginBottom:"1.8rem", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"1rem" }}>
          <div>
            <div className="dash-welcome-title" style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.7rem" }}>
              👋 Welcome, {parentInfo.name.split(" ")[0] || "Parent"}!
            </div>
            <div style={{ color:"rgba(255,255,255,0.45)", fontSize:"0.86rem", fontWeight:600, marginTop:3 }}>
              Managing <strong style={{ color:"#FFD93D" }}>{students.length}</strong> student{students.length !== 1 ? "s" : ""} · {enrolled.length} enrollment{enrolled.length !== 1 ? "s" : ""}
            </div>
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <Badge color="#22C55E">{enrolled.length} Enrolled</Badge>
            <Badge color="#FFD93D">{Object.keys(paid).length} Paid</Badge>
          </div>
        </div>

        {/* ── Loading state ── */}
        {studentsLoading && (
          <div style={{ textAlign:"center", padding:"4rem 2rem", color:"rgba(255,255,255,0.3)", fontWeight:600, fontSize:"0.95rem" }}>
            Loading your students…
          </div>
        )}

        {/* ── No-student gate ── */}
        {!studentsLoading && students.length === 0 && (
          <div style={{ textAlign:"center", padding:"4rem 2rem", background:"rgba(255,255,255,0.03)", border:"1.5px dashed rgba(255,217,61,0.25)", borderRadius:24 }}>
            <div style={{ fontSize:"3.5rem", marginBottom:"1rem" }}>🧑‍🚀</div>
            <h3 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.6rem", marginBottom:"0.6rem" }}>Add your first student to get started</h3>
            <p style={{ color:"rgba(255,255,255,0.45)", fontSize:"0.92rem", fontWeight:600, lineHeight:1.7, maxWidth:420, margin:"0 auto 1.8rem" }}>
              You need to add at least one student before you can view enrollment options, schedules, or account details.
            </p>
            <Btn onClick={() => setShowAddStudent(true)} variant="primary" style={{ fontSize:"1rem", padding:"13px 36px" }}>
              + Add a Student
            </Btn>
          </div>
        )}

        {/* ── Tabs (only shown when at least one student exists) ── */}
        {!studentsLoading && students.length > 0 && (
        <div className="dash-tabs" style={{ display:"flex", gap:5, marginBottom:"1.6rem", background:"rgba(255,255,255,0.05)", borderRadius:14, padding:5, width:"fit-content", flexWrap:"wrap" }}>
          {[["enroll","📋 Enroll"],["schedule","📅 Schedule"],["account","👤 My Account"]].map(([id, label]) => (
            <button key={id} onClick={() => setActiveTab(id)} style={{ padding:"8px 18px", borderRadius:10, border:"none", cursor:"pointer", background: activeTab===id?"#FFD93D":"transparent", color: activeTab===id?"#0B0E2A":"rgba(255,255,255,0.5)", fontFamily:"inherit", fontWeight:800, fontSize:"0.87rem", transition:"all 0.2s", whiteSpace:"nowrap" }}>{label}</button>
          ))}
        </div>
        )}

        {/* ════════════════════════════════════
            TAB CONTENT (hidden until student added)
        ════════════════════════════════════ */}
        {!studentsLoading && students.length > 0 && activeTab === "enroll" && (
          <div>
            {/* Student switcher */}
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:"1rem" }}>
              {students.map((s, i) => (
                <button key={i} onClick={() => setSelStudentIdx(i)} style={{ padding:"7px 18px", borderRadius:50, border:`1.5px solid ${selStudentIdx===i?"#FFD93D":"rgba(255,255,255,0.15)"}`, background: selStudentIdx===i?"rgba(255,217,61,0.12)":"rgba(255,255,255,0.04)", color: selStudentIdx===i?"#FFD93D":"rgba(255,255,255,0.65)", fontFamily:"inherit", fontWeight:800, fontSize:"0.85rem", cursor:"pointer", transition:"all 0.2s" }}>
                  🧑‍🚀 {s.name || `Student ${i + 1}`}
                </button>
              ))}
              <button onClick={() => setShowAddStudent(true)} style={{ padding:"7px 18px", borderRadius:50, border:"1.5px dashed rgba(255,217,61,0.4)", background:"rgba(255,217,61,0.06)", color:"rgba(255,217,61,0.8)", fontFamily:"inherit", fontWeight:800, fontSize:"0.85rem", cursor:"pointer" }}>+ Add Student</button>
            </div>

            {/* Sibling discount notice */}
            {selPriceInfo.discount && (
              <div style={{ background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.3)", borderRadius:12, padding:"0.7rem 1.2rem", marginBottom:"1rem", fontSize:"0.85rem", fontWeight:700, color:"#22C55E" }}>
                🎉 Sibling discount applied — 10% off for {students[selStudentIdx]?.name || `Student ${selStudentIdx + 1}`}!
              </div>
            )}

            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(290px,1fr))", gap:"1.4rem" }}>

              {/* School — independent per student */}
              <GlassCard hover={false}>
                <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem", marginBottom:"1.1rem" }}>🏫 Select School</div>
                <FormSelect
                  value={curEnroll.selSchool}
                  onChange={(e) => { setEnrollField(selStudentIdx, "selSchool", e.target.value); setEnrollField(selStudentIdx, "selSession", ""); }}
                  placeholder="Choose a school..."
                  options={schools
                    .filter((s) => allSessions.some((sess) => sess.school_id == s.id))
                    .map((s) => ({ value: s.id, label: `${s.name} — ${s.location}` }))}
                />
                {curEnroll.selSchool && (
                  <div style={{ marginTop:"0.9rem", display:"flex", gap:8, alignItems:"center" }}>
                    <span>📍</span>
                    <span style={{ color:"rgba(255,255,255,0.45)", fontSize:"0.83rem", fontWeight:600 }}>
                      {schools.find((s) => s.id == curEnroll.selSchool)?.location}
                    </span>
                  </div>
                )}
              </GlassCard>

              {/* Session — independent per student */}
              <GlassCard hover={false}>
                <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem", marginBottom:"1.1rem" }}>📅 Select Session</div>
                {!curEnroll.selSchool
                  ? <p style={{ color:"rgba(255,255,255,0.3)", fontSize:"0.86rem", fontWeight:600 }}>Select a school first.</p>
                  : <div style={{ display:"flex", flexDirection:"column", gap:"0.7rem" }}>
                    {sessions.map((s) => {
                      const enr = isEnrolled(s.id, selStudentIdx);
                      const isFull = s.spots > 0 && (s.enrolled_count ?? 0) >= s.spots;
                      const { price: displayPrice, discount } = getPrice(s, selStudentIdx, enrolled);
                      const isSelected = curEnroll.selSession === s.id;
                      return (
                        <div key={s.id} onClick={() => !enr && !isFull && setEnrollField(selStudentIdx, "selSession", s.id)}
                          style={{ border:`1.5px solid ${isSelected?"#FFD93D":enr?"#22C55E44":isFull?"rgba(255,107,107,0.25)":"rgba(255,255,255,0.1)"}`, borderRadius:13, padding:"0.95rem 1rem", cursor: enr||isFull?"default":"pointer", background: isSelected?"rgba(255,217,61,0.07)":enr?"rgba(34,197,94,0.05)":isFull?"rgba(255,107,107,0.04)":"rgba(255,255,255,0.025)", opacity: enr||isFull?0.68:1, transition:"all 0.2s" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:6, marginBottom:4 }}>
                            <div style={{ fontWeight:800, fontSize:"0.88rem" }}>{s.title}</div>
                            {enr
                              ? paid[enrollKey(s.id, selStudentIdx)]
                                ? <Badge color="#22C55E" small>Enrolled</Badge>
                                : <Badge color="#F59E0B" small>In Cart</Badge>
                              : isFull
                                ? <Badge color="#FF6B6B" small>Full Class</Badge>
                                : s.price === 0
                                  ? <Badge color="#22C55E" small>FREE</Badge>
                                  : <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:2 }}>
                                    {discount && <span style={{ fontSize:"0.65rem", color:"rgba(255,255,255,0.35)", textDecoration:"line-through" }}>${s.price}</span>}
                                    <Badge color={discount?"#22C55E":"#FFD93D"} small>{discount?`$${displayPrice} (-10%)`:`$${displayPrice} each`}</Badge>
                                  </div>
                            }
                          </div>
                          <div style={{ color:"rgba(255,255,255,0.4)", fontSize:"0.77rem", fontWeight:600, marginBottom:4 }}>{s.days} · {s.time} · {s.dates}</div>
                          {isFull && <Badge color="#FF6B6B" small>Full Class</Badge>}
                        </div>
                      );
                    })}
                  </div>
                }
              </GlassCard>

              {/* Pricing summary */}
              <GlassCard hover={false}>
                <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem", marginBottom:"1.1rem" }}>💳 Enrollment & Pricing</div>
                {!selSession
                  ? <p style={{ color:"rgba(255,255,255,0.3)", fontSize:"0.86rem", fontWeight:600 }}>Select a session to see pricing.</p>
                  : isEnrolled(selSession.id, selStudentIdx)
                    ? paid[enrollKey(selSession.id, selStudentIdx)]
                      ? <div style={{ textAlign:"center", padding:"1.2rem 0" }}>
                          <div style={{ fontSize:"2.2rem", marginBottom:"0.5rem" }}>✅</div>
                          <div style={{ fontWeight:800, color:"#22C55E", marginBottom:4 }}>Already Enrolled!</div>
                          <div style={{ color:"rgba(255,255,255,0.4)", fontSize:"0.82rem", fontWeight:600 }}>Payment confirmed ✓</div>
                        </div>
                      : <div style={{ textAlign:"center", padding:"1.2rem 0" }}>
                          <div style={{ fontSize:"2.2rem", marginBottom:"0.5rem" }}>🛒</div>
                          <div style={{ fontWeight:800, color:"#F59E0B", marginBottom:4 }}>Added to Cart!</div>
                          <div style={{ color:"rgba(255,255,255,0.4)", fontSize:"0.82rem", fontWeight:600 }}>Scroll down to checkout or add more sessions.</div>
                          <Btn onClick={() => { setEnrolled((prev) => prev.filter((e) => e.key !== enrollKey(selSession.id, selStudentIdx))); }} variant="ghost" style={{ marginTop:"0.8rem", fontSize:"0.75rem", padding:"5px 14px" }}>Remove from Cart</Btn>
                        </div>
                  : selSession.spots > 0 && (selSession.enrolled_count ?? 0) >= selSession.spots
                    ? <div style={{ textAlign:"center", padding:"1.2rem 0" }}>
                      <div style={{ fontSize:"2.2rem", marginBottom:"0.5rem" }}>🚫</div>
                      <div style={{ fontWeight:800, color:"#FF6B6B", marginBottom:4 }}>Class Full</div>
                      <div style={{ color:"rgba(255,255,255,0.4)", fontSize:"0.82rem", fontWeight:600 }}>
                        All {selSession.spots} spots have been filled.
                      </div>
                    </div>
                    : <>
                      <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:13, padding:"1rem", marginBottom:"1rem" }}>
                        {[
                          ["Session",  selSession.title],
                          ["Student",  students[selStudentIdx]?.name || `Student ${selStudentIdx + 1}`],
                          ["Schedule", `${selSession.days} · ${selSession.time}`],
                          ["Dates",    selSession.dates],
                        ].map(([k, v]) => (
                          <div key={k} style={{ display:"flex", justifyContent:"space-between", marginBottom:6, gap:8 }}>
                            <span style={{ color:"rgba(255,255,255,0.42)", fontSize:"0.82rem", fontWeight:600 }}>{k}</span>
                            <span style={{ fontWeight:700, fontSize:"0.82rem", textAlign:"right" }}>{v}</span>
                          </div>
                        ))}
                        {selPriceInfo.discount && selSession.price > 0 && (
                          <div style={{ display:"flex", justifyContent:"space-between", color:"#22C55E", fontSize:"0.82rem", marginBottom:6 }}>
                            <span style={{ fontWeight:700 }}>Sibling Discount</span>
                            <span style={{ fontWeight:800 }}>-10%</span>
                          </div>
                        )}
                        <div style={{ display:"flex", justifyContent:"space-between", borderTop:"1px solid rgba(255,255,255,0.1)", paddingTop:8, marginTop:4 }}>
                          <span style={{ fontWeight:800 }}>Total</span>
                          <div style={{ textAlign:"right" }}>
                            {selSession.price === 0
                              ? <span style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.4rem", color:"#22C55E" }}>FREE</span>
                              : <>
                                {selPriceInfo.discount && <div style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.35)", textDecoration:"line-through" }}>${selSession.price}</div>}
                                <span style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.4rem", color:"#FFD93D" }}>${selPriceInfo.price}</span>
                                <div style={{ fontSize:"0.7rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>per student</div>
                              </>
                            }
                          </div>
                        </div>
                      </div>
                      <Btn onClick={doEnroll} variant="primary" style={{ width:"100%" }}>
                        {selSession.price === 0 ? "🚀 Enroll Free" : "🛒 Add to Cart"}
                      </Btn>
                    </>
                }
              </GlassCard>
            </div>

            {/* ── Cart section ── */}
            {cartItemsWithPrices.length > 0 && (
              <GlassCard hover={false} style={{ marginTop:"1.4rem", border:"1.5px solid rgba(255,217,61,0.25)" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.1rem", flexWrap:"wrap", gap:8 }}>
                  <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem" }}>
                    🛒 Your Cart <span style={{ fontSize:"0.9rem", color:"rgba(255,217,61,0.7)" }}>({cartItemsWithPrices.length} session{cartItemsWithPrices.length !== 1 ? "s" : ""})</span>
                  </div>
                  <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.1rem", color:"#FFD93D" }}>
                    Total: ${cartTotal}
                  </div>
                </div>

                <div style={{ display:"flex", flexDirection:"column", gap:"0.6rem", marginBottom:"1.1rem" }}>
                  {cartItemsWithPrices.map((item) => (
                    <div key={item.enrollment.key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:"rgba(255,255,255,0.04)", borderRadius:11, padding:"0.7rem 1rem", gap:8 }}>
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{ fontWeight:800, fontSize:"0.86rem", marginBottom:2 }}>{item.session.title}</div>
                        <div style={{ color:"rgba(255,255,255,0.4)", fontSize:"0.75rem", fontWeight:600 }}>
                          🧑‍🚀 {students[item.enrollment.studentIdx]?.name || `Student ${item.enrollment.studentIdx + 1}`}
                          {" · "}{item.session.days} · {item.session.time}
                        </div>
                        {item.priceInfo.discount && (
                          <div style={{ color:"#22C55E", fontSize:"0.72rem", fontWeight:700, marginTop:2 }}>Sibling discount applied — 10% off</div>
                        )}
                      </div>
                      <div style={{ textAlign:"right", flexShrink:0, marginRight:8 }}>
                        {item.priceInfo.discount && (
                          <div style={{ fontSize:"0.7rem", color:"rgba(255,255,255,0.3)", textDecoration:"line-through" }}>${item.session.price}</div>
                        )}
                        <div style={{ fontWeight:800, color:"#FFD93D" }}>${item.priceInfo.price}</div>
                      </div>
                      <button
                        onClick={() => setEnrolled((prev) => prev.filter((e) => e.key !== item.enrollment.key))}
                        style={{ background:"rgba(255,107,107,0.1)", border:"1px solid rgba(255,107,107,0.25)", color:"rgba(255,107,107,0.7)", borderRadius:8, padding:"4px 10px", fontSize:"0.75rem", cursor:"pointer", fontFamily:"inherit", fontWeight:700, whiteSpace:"nowrap" }}
                      >Remove</button>
                    </div>
                  ))}
                </div>

                <div style={{ borderTop:"1px solid rgba(255,255,255,0.1)", paddingTop:"1rem", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
                  <div style={{ fontSize:"0.82rem", color:"rgba(255,255,255,0.42)", fontWeight:600 }}>
                    You'll enter your card once for all sessions.
                  </div>
                  <div style={{ display:"flex", gap:8 }}>
                    <Btn onClick={() => setEnrolled((prev) => prev.filter((e) => e.dbId))} variant="ghost" style={{ fontSize:"0.82rem", padding:"7px 16px" }}>Clear Cart</Btn>
                    <Btn onClick={() => setCartCheckoutOpen(true)} variant="primary" style={{ fontSize:"0.92rem", padding:"9px 24px" }}>
                      🚀 Checkout — ${cartTotal}
                    </Btn>
                  </div>
                </div>
              </GlassCard>
            )}
          </div>
        )}

        {/* ════════════════════════════════════
            SCHEDULE TAB
        ════════════════════════════════════ */}
        {!studentsLoading && students.length > 0 && activeTab === "schedule" && (
          <GlassCard hover={false}>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.3rem", marginBottom:"1.4rem" }}>📅 Enrolled Sessions</div>
            {sessionsLoading ? (
              <div style={{ textAlign:"center", padding:"3rem 1rem", color:"rgba(255,255,255,0.3)", fontWeight:600, fontSize:"0.95rem" }}>
                Loading sessions…
              </div>
            ) : scheduleEnrollments.length === 0 ? (
              <div style={{ textAlign:"center", padding:"3rem 1rem" }}>
                <div style={{ fontSize:"3rem", marginBottom:"0.8rem" }}>🛸</div>
                <p style={{ color:"rgba(255,255,255,0.36)", fontWeight:600 }}>No confirmed enrollments yet.</p>
                <Btn onClick={() => setActiveTab("enroll")} variant="ghost" style={{ marginTop:"1rem" }}>Go Enroll →</Btn>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:"0.9rem" }}>
                {scheduleEnrollments.map((e) => {
                  const sd = findSession(e.sessionId, allSessions, enrolledSessionDetails);
                  if (!sd) return null;
                  const isPaid = paid[e.key];
                  const student = students[e.studentIdx];
                  const { price: finalPrice, discount } = getPrice(sd, e.studentIdx, scheduleEnrollments.filter((x) => x.key !== e.key));
                  return (
                    <div key={e.key} className="dash-schedule-row" style={{ display:"grid", gridTemplateColumns:"auto 1fr auto", gap:"0.75rem", alignItems:"center", background:"rgba(255,255,255,0.04)", borderRadius:14, padding:"1rem 1.2rem", borderLeft:"4px solid #9B5CF6" }}>
                      <div style={{ width:44, height:44, borderRadius:13, background:"rgba(155,92,246,0.18)", border:"2px solid rgba(155,92,246,0.35)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.3rem" }}>🛸</div>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontWeight:800, fontSize:"0.92rem", marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{sd.title}</div>
                        <div style={{ color:"rgba(255,255,255,0.42)", fontSize:"0.79rem", fontWeight:600 }}>{sd.days} · {sd.time}</div>
                        <div style={{ color:"rgba(255,255,255,0.35)", fontSize:"0.72rem", fontWeight:600 }}>{sd.dates}</div>
                        <div style={{ marginTop:5, display:"flex", gap:6, flexWrap:"wrap" }}>
                          <Badge color="#FFD93D" small>🧑‍🚀 {student?.name || `Student ${e.studentIdx + 1}`}</Badge>
                          {sd.price === 0
                            ? <Badge color="#22C55E" small>FREE</Badge>
                            : <>
                              {discount && <Badge color="#22C55E" small>Sibling -10%</Badge>}
                              <Badge color="#9B5CF6" small>${finalPrice}</Badge>
                            </>
                          }
                        </div>
                      </div>
                      <div className="dash-schedule-actions" style={{ textAlign:"right" }}>
                        {sd.price === 0
                          ? <Badge color="#22C55E" small>✓ Enrolled</Badge>
                          : <>
                            <Badge color={isPaid ? "#22C55E" : "#F59E0B"} small>{isPaid ? "✓ Paid" : "⏳ Pending"}</Badge>
                            {!isPaid && <div style={{ marginTop:7 }}><Btn onClick={() => setPayStep(e.key)} variant="primary" style={{ padding:"5px 14px", fontSize:"0.78rem" }}>Pay ${finalPrice}</Btn></div>}
                          </>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </GlassCard>
        )}

        {/* ════════════════════════════════════
            ACCOUNT TAB
        ════════════════════════════════════ */}
        {!studentsLoading && students.length > 0 && activeTab === "account" && (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:"1.4rem" }}>
            {/* Parent info */}
            <GlassCard hover={false}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.2rem" }}>
                <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem" }}>👤 Parent Info</div>
                <button onClick={() => openEdit("parent")} style={{ background:"rgba(255,217,61,0.1)", border:"1px solid rgba(255,217,61,0.3)", color:"#FFD93D", borderRadius:10, padding:"4px 14px", fontSize:"0.78rem", cursor:"pointer", fontFamily:"inherit", fontWeight:800 }}>Edit</button>
              </div>
              {[["Name", parentInfo.name],["Email", parentInfo.email],["Phone", parentInfo.phone]].map(([k, v]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"0.55rem 0", borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
                  <span style={{ color:"rgba(255,255,255,0.42)", fontSize:"0.83rem", fontWeight:600 }}>{k}</span>
                  <span style={{ fontSize:"0.88rem", fontWeight:700 }}>{v || "—"}</span>
                </div>
              ))}
            </GlassCard>

            {/* Student cards */}
            {students.map((s, i) => (
              <GlassCard key={i} hover={false}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.2rem" }}>
                  <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem" }}>🧑‍🚀 {s.name || `Student ${i + 1}`}</div>
                  <button onClick={() => openEdit(i)} style={{ background:"rgba(255,217,61,0.1)", border:"1px solid rgba(255,217,61,0.3)", color:"#FFD93D", borderRadius:10, padding:"4px 14px", fontSize:"0.78rem", cursor:"pointer", fontFamily:"inherit", fontWeight:800 }}>Edit</button>
                </div>
                {[["Grade", s.grade],["Age", s.age],["Allergies", s.allergies],["Accommodations", s.needs],["Tech Experience", { none:"None", beginner:"Beginner", intermediate:"Intermediate", advanced:"Advanced" }[s.tech_level] ?? null]].map(([k, v]) => (
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"0.55rem 0", borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
                    <span style={{ color:"rgba(255,255,255,0.42)", fontSize:"0.83rem", fontWeight:600 }}>{k}</span>
                    <span style={{ fontSize:"0.88rem", fontWeight:700 }}>{v || "—"}</span>
                  </div>
                ))}
                {s.tech_notes && (
                  <div style={{ padding:"0.55rem 0" }}>
                    <div style={{ color:"rgba(255,255,255,0.42)", fontSize:"0.78rem", fontWeight:600, marginBottom:3 }}>Tech Notes</div>
                    <div style={{ fontSize:"0.85rem", fontWeight:600, color:"rgba(255,255,255,0.75)", lineHeight:1.5 }}>{s.tech_notes}</div>
                  </div>
                )}
              </GlassCard>
            ))}

            {/* Add student tile */}
            <div onClick={() => setShowAddStudent(true)} style={{ border:"1.5px dashed rgba(255,217,61,0.3)", borderRadius:20, padding:"1.75rem", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"0.5rem", cursor:"pointer", transition:"background 0.2s" }} onMouseEnter={(e) => (e.currentTarget.style.background="rgba(255,217,61,0.05)")} onMouseLeave={(e) => (e.currentTarget.style.background="transparent")}>
              <span style={{ fontSize:"2rem" }}>➕</span>
              <span style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1rem", color:"rgba(255,217,61,0.75)" }}>Add a Student</span>
            </div>
          </div>
        )}
      </div>

      {/* ── CART CHECKOUT MODAL ── */}
      {cartCheckoutOpen && cartItemsWithPrices.length > 0 && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
          <div style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(255,255,255,0.14)", borderRadius:24, padding:"2.2rem", maxWidth:460, width:"100%", backdropFilter:"blur(20px)", maxHeight:"90vh", overflowY:"auto" }}>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.5rem", marginBottom:"0.2rem" }}>💳 Checkout</div>
            <p style={{ color:"rgba(255,255,255,0.38)", fontSize:"0.82rem", fontWeight:600, marginBottom:"1rem" }}>
              Secure checkout — powered by Stripe
            </p>

            {/* Promo code */}
            <div style={{ marginBottom:"1rem" }}>
              <div style={{ fontSize:"0.75rem", fontWeight:700, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>Promo Code</div>
              {cartAppliedPromo ? (
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"rgba(34,197,94,0.1)", border:"1.5px solid rgba(34,197,94,0.35)", borderRadius:10, padding:"8px 14px" }}>
                  <span style={{ fontWeight:800, color:"#22C55E", fontSize:"0.88rem" }}>🎟️ {cartAppliedPromo.code} — -${cartAppliedPromo.discountAmount} off first session</span>
                  <button onClick={() => { setCartAppliedPromo(null); setCartPromoInput(""); setCartPromoError(""); }} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", fontSize:"1rem", lineHeight:1, padding:"0 0 0 8px" }}>✕</button>
                </div>
              ) : (
                <div style={{ display:"flex", gap:6 }}>
                  <input
                    value={cartPromoInput}
                    onChange={(e) => setCartPromoInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && cartApplyPromo()}
                    placeholder="Enter code…"
                    style={{ flex:1, background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.14)", borderRadius:10, padding:"9px 14px", color:"#fff", fontFamily:"inherit", fontWeight:600, fontSize:"0.88rem", outline:"none" }}
                  />
                  <button
                    onClick={cartApplyPromo}
                    disabled={cartPromoChecking || !cartPromoInput.trim()}
                    style={{ background: cartPromoChecking || !cartPromoInput.trim() ? "rgba(255,255,255,0.05)" : "rgba(155,92,246,0.2)", border:`1.5px solid ${cartPromoChecking || !cartPromoInput.trim() ? "rgba(255,255,255,0.1)" : "rgba(155,92,246,0.5)"}`, color: cartPromoChecking || !cartPromoInput.trim() ? "rgba(255,255,255,0.3)" : "#C084FC", borderRadius:10, padding:"9px 16px", cursor: cartPromoChecking || !cartPromoInput.trim() ? "default" : "pointer", fontFamily:"inherit", fontWeight:800, fontSize:"0.84rem", whiteSpace:"nowrap" }}>
                    {cartPromoChecking ? "…" : "Apply"}
                  </button>
                </div>
              )}
              {cartPromoError && <div style={{ color:"#FF6B6B", fontSize:"0.78rem", fontWeight:700, marginTop:5 }}>{cartPromoError}</div>}
            </div>

            <Elements stripe={stripePromise} options={{ appearance: { theme: "night" } }}>
              <CartPaymentForm
                cartItems={cartItemsWithPrices}
                students={students}
                parentInfo={parentInfo}
                schools={schools}
                appliedPromo={cartAppliedPromo}
                onAllSuccess={(completed) => {
                  completed.forEach(({ key, dbRow }) => {
                    setEnrolled((prev) => prev.map((e) => e.key === key ? { ...e, dbId: dbRow.id } : e));
                    setPaid((prev) => ({ ...prev, [key]: true }));
                  });
                  setCartCheckoutOpen(false);
                  setCartAppliedPromo(null);
                  setCartPromoInput("");
                  setCartPromoError("");
                  setActiveTab("schedule");
                }}
                onCancel={() => {
                  setCartCheckoutOpen(false);
                  setCartAppliedPromo(null);
                  setCartPromoInput("");
                  setCartPromoError("");
                }}
              />
            </Elements>
          </div>
        </div>
      )}

      {/* ── EDIT MODAL ── */}
      {editModal !== null && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
          <div style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(255,255,255,0.14)", borderRadius:24, padding:"2.2rem", maxWidth:460, width:"100%", backdropFilter:"blur(20px)" }}>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.5rem", marginBottom:"1.2rem" }}>
              {editModal === "parent" ? "✏️ Edit Parent Info" : `✏️ Edit ${students[editModal]?.name || "Student"}`}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:"0.85rem" }}>
              {editModal === "parent" ? (
                <>
                  <FormInput label="Full Name" value={editData.name||""} onChange={(e) => setEditData((p) => ({ ...p, name:e.target.value }))} placeholder="Jane Smith" />
                  <div>
                    <FormInput label="Email" type="email" value={editData.email||""} onChange={(e) => setEditData((p) => ({ ...p, email:e.target.value }))} placeholder="parent@email.com" />
                    <div style={{ fontSize:"0.74rem", color:"rgba(255,255,255,0.35)", fontWeight:600, marginTop:4 }}>
                      This is your contact email — it won't change the email you use to log in.
                    </div>
                  </div>
                  <FormInput label="Phone" value={editData.phone||""} onChange={(e) => setEditData((p) => ({ ...p, phone:e.target.value }))} placeholder="(555) 000-0000" />
                </>
              ) : (
                <>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:"0.85rem" }}>
                    <FormInput label="Full Name" value={editData.name||""} onChange={(e) => setEditData((p) => ({ ...p, name:e.target.value }))} placeholder="Alex Smith" half />
                    <FormInput label="Age" type="number" value={editData.age||""} onChange={(e) => setEditData((p) => ({ ...p, age:e.target.value }))} placeholder="9" half />
                  </div>
                  <FormSelect label="Grade" value={editData.grade||""} onChange={(e) => setEditData((p) => ({ ...p, grade:e.target.value }))} placeholder="Select grade..." options={GRADE_OPTIONS} />
                  <FormInput label="Allergies / Medical Notes" value={editData.allergies||""} onChange={(e) => setEditData((p) => ({ ...p, allergies:e.target.value }))} placeholder="None, or describe" />
                  <FormInput label="Accommodations" value={editData.needs||""} onChange={(e) => setEditData((p) => ({ ...p, needs:e.target.value }))} placeholder="Optional" />

                  {/* Tech experience section */}
                  <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:"0.85rem", marginTop:"0.1rem" }}>
                    <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1rem", marginBottom:"0.7rem", color:"rgba(255,255,255,0.8)" }}>💻 Technology Experience</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:"0.75rem" }}>
                      <FormSelect
                        label="Previous Experience Level"
                        value={editData.tech_level||""}
                        onChange={(e) => setEditData((p) => ({ ...p, tech_level:e.target.value }))}
                        placeholder="Select a level..."
                        options={[
                          { value:"none",         label:"None — No previous experience" },
                          { value:"beginner",     label:"Beginner — Uses tablets, games, basic computer skills" },
                          { value:"intermediate", label:"Intermediate — Has tried coding or robotics" },
                          { value:"advanced",     label:"Advanced — Regular coding, builds projects" },
                        ]}
                      />
                      <div>
                        <div style={{ fontSize:"0.78rem", fontWeight:700, color:"rgba(255,255,255,0.45)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:5 }}>Details <span style={{ fontWeight:600, color:"rgba(255,255,255,0.28)", textTransform:"none", letterSpacing:0 }}>(optional)</span></div>
                        <textarea
                          value={editData.tech_notes||""}
                          onChange={(e) => setEditData((p) => ({ ...p, tech_notes:e.target.value }))}
                          placeholder="e.g. Has used Scratch, tried Python, built a Lego Mindstorms robot…"
                          rows={2}
                          style={{ width:"100%", background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.14)", borderRadius:10, padding:"10px 14px", color:"#fff", fontFamily:"inherit", fontWeight:600, fontSize:"0.88rem", outline:"none", resize:"vertical", boxSizing:"border-box" }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div style={{ display:"flex", gap:"0.7rem", marginTop:"0.3rem" }}>
                <Btn onClick={() => setEditModal(null)} variant="ghost" style={{ flex:"0 0 auto" }}>Cancel</Btn>
                <Btn onClick={saveEdit} disabled={saving} variant="success" style={{ flex:1 }}>{saving ? "Saving…" : "Save Changes ✓"}</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD STUDENT MODAL ── */}
      {showAddStudent && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
          <div style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(255,255,255,0.14)", borderRadius:24, padding:"2.2rem", maxWidth:480, width:"100%", backdropFilter:"blur(20px)" }}>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.5rem", marginBottom:"0.5rem" }}>🧑‍🚀 Add a Student</div>
            <div style={{ display:"flex", flexDirection:"column", gap:"0.85rem" }}>
              <div style={{ display:"flex", flexWrap:"wrap", gap:"0.85rem" }}>
                <FormInput label="Full Name" value={newStudent.name} onChange={(e) => setNewStudent((p) => ({ ...p, name:e.target.value }))} placeholder="Jordan Smith" required half />
                <FormInput label="Age" type="number" value={newStudent.age} onChange={(e) => setNewStudent((p) => ({ ...p, age:e.target.value }))} placeholder="9" half />
              </div>
              <FormSelect label="Grade" value={newStudent.grade} onChange={(e) => setNewStudent((p) => ({ ...p, grade:e.target.value }))} placeholder="Select grade..." options={GRADE_OPTIONS} />
              <FormInput label="Allergies / Medical Notes" value={newStudent.allergies} onChange={(e) => setNewStudent((p) => ({ ...p, allergies:e.target.value }))} placeholder="None, or list here" />
              <FormInput label="Learning Accommodations" value={newStudent.needs} onChange={(e) => setNewStudent((p) => ({ ...p, needs:e.target.value }))} placeholder="Optional" />

              {/* Tech experience section */}
              <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:"0.85rem", marginTop:"0.1rem" }}>
                <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1rem", marginBottom:"0.7rem", color:"rgba(255,255,255,0.8)" }}>💻 Technology Experience</div>
                <div style={{ display:"flex", flexDirection:"column", gap:"0.75rem" }}>
                  <FormSelect
                    label="Previous Experience Level"
                    value={newStudent.tech_level}
                    onChange={(e) => setNewStudent((p) => ({ ...p, tech_level:e.target.value }))}
                    placeholder="Select a level..."
                    options={[
                      { value:"none",         label:"None — No previous experience" },
                      { value:"beginner",     label:"Beginner — Uses tablets, games, basic computer skills" },
                      { value:"intermediate", label:"Intermediate — Has tried coding or robotics" },
                      { value:"advanced",     label:"Advanced — Regular coding, builds projects" },
                    ]}
                  />
                  <div>
                    <div style={{ fontSize:"0.78rem", fontWeight:700, color:"rgba(255,255,255,0.45)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:5 }}>Details <span style={{ fontWeight:600, color:"rgba(255,255,255,0.28)", textTransform:"none", letterSpacing:0 }}>(optional)</span></div>
                    <textarea
                      value={newStudent.tech_notes}
                      onChange={(e) => setNewStudent((p) => ({ ...p, tech_notes:e.target.value }))}
                      placeholder="e.g. Has used Scratch, tried Python, built a Lego Mindstorms robot…"
                      rows={2}
                      style={{ width:"100%", background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.14)", borderRadius:10, padding:"10px 14px", color:"#fff", fontFamily:"inherit", fontWeight:600, fontSize:"0.88rem", outline:"none", resize:"vertical", boxSizing:"border-box" }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display:"flex", gap:"0.7rem", marginTop:"0.3rem" }}>
                <Btn onClick={() => setShowAddStudent(false)} variant="ghost" style={{ flex:"0 0 auto" }}>Cancel</Btn>
                <Btn onClick={doAddStudent} disabled={saving} variant="success" style={{ flex:1 }}>{saving ? "Saving…" : "+ Add Student"}</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── FREE ENROLLMENT CONFIRMATION MODAL ── */}
      {freeConfirmKey && freeConfirmSession && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
          <div style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(34,197,94,0.3)", borderRadius:24, padding:"2.2rem", maxWidth:420, width:"100%", backdropFilter:"blur(20px)" }}>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.5rem", marginBottom:"0.2rem" }}>🎉 Free Class!</div>
            <p style={{ color:"rgba(255,255,255,0.38)", fontSize:"0.82rem", fontWeight:600, marginBottom:"1.2rem" }}>
              No payment needed — confirm your spot below.
            </p>
            <div style={{ background:"rgba(34,197,94,0.06)", border:"1px solid rgba(34,197,94,0.2)", borderRadius:12, padding:"0.9rem 1rem", marginBottom:"1.2rem" }}>
              {[
                ["Session",  freeConfirmSession.title],
                ["Student",  students[freeConfirmEnrollment?.studentIdx]?.name || "Student"],
                ["Schedule", `${freeConfirmSession.days} · ${freeConfirmSession.time}`],
                ["Dates",    freeConfirmSession.dates],
              ].map(([k, v]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", marginBottom:6, gap:8 }}>
                  <span style={{ color:"rgba(255,255,255,0.42)", fontSize:"0.82rem", fontWeight:600 }}>{k}</span>
                  <span style={{ fontWeight:700, fontSize:"0.82rem", textAlign:"right" }}>{v}</span>
                </div>
              ))}
              <div style={{ display:"flex", justifyContent:"space-between", borderTop:"1px solid rgba(255,255,255,0.1)", paddingTop:8, marginTop:4 }}>
                <span style={{ fontWeight:800 }}>Cost</span>
                <span style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.3rem", color:"#22C55E" }}>FREE</span>
              </div>
            </div>
            {freeConfirmError && (
              <div style={{ background:"rgba(255,107,107,0.1)", border:"1px solid rgba(255,107,107,0.3)", borderRadius:10, padding:"0.7rem 1rem", fontSize:"0.83rem", fontWeight:700, color:"#FF6B6B", marginBottom:"0.8rem" }}>
                {freeConfirmError}
              </div>
            )}
            <div style={{ display:"flex", gap:"0.7rem" }}>
              <Btn onClick={() => { setEnrolled((prev) => prev.filter((e) => e.key !== freeConfirmKey)); setFreeConfirmKey(null); setFreeConfirmError(""); }} variant="ghost" style={{ flex:"0 0 auto" }} disabled={freeConfirmSaving}>Cancel</Btn>
              <Btn onClick={doFreeEnroll} variant="success" style={{ flex:1 }} disabled={freeConfirmSaving}>
                {freeConfirmSaving ? "Enrolling…" : "✓ Confirm Enrollment"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── PAYMENT MODAL ── */}
      {payStep && payingSessionData && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
          <div style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(255,255,255,0.14)", borderRadius:24, padding:"2.2rem", maxWidth:420, width:"100%", backdropFilter:"blur(20px)" }}>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.5rem", marginBottom:"0.2rem" }}>💳 Complete Payment</div>
            <p style={{ color:"rgba(255,255,255,0.38)", fontSize:"0.82rem", fontWeight:600, marginBottom:"0.8rem" }}>
              Secure checkout — powered by Stripe
            </p>
            {/* Session + price summary */}
            <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:12, padding:"0.8rem 1rem", marginBottom:"1rem" }}>
              <div style={{ fontWeight:700, fontSize:"0.88rem", marginBottom:2 }}>{payingSessionData.title}</div>
              <div style={{ color:"rgba(255,255,255,0.38)", fontSize:"0.75rem", fontWeight:600, marginBottom:6 }}>
                For: {students[payingEnrollment?.studentIdx]?.name || "Student"}
              </div>
              {payingPriceInfo.discount && <div style={{ color:"#22C55E", fontSize:"0.78rem", fontWeight:700, marginBottom:4 }}>Sibling discount applied — 10% off</div>}
              {payingPriceInfo.promoDiscount > 0 && (
                <div style={{ color:"#22C55E", fontSize:"0.78rem", fontWeight:700, marginBottom:4 }}>
                  🎟️ Promo {appliedPromo?.code} — -${payingPriceInfo.promoDiscount} off
                </div>
              )}
              <div style={{ display:"flex", justifyContent:"space-between", borderTop:"1px solid rgba(255,255,255,0.1)", paddingTop:8 }}>
                <span style={{ fontWeight:800 }}>Total</span>
                <div style={{ textAlign:"right" }}>
                  {(payingPriceInfo.discount || payingPriceInfo.promoDiscount > 0) && (
                    <div style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.35)", textDecoration:"line-through" }}>${payingSessionData.price}</div>
                  )}
                  {payingPriceInfo.price === 0
                    ? <span style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.3rem", color:"#22C55E" }}>FREE</span>
                    : <span style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.3rem", color:"#FFD93D" }}>${payingPriceInfo.price}</span>
                  }
                  {payingPriceInfo.price > 0 && <div style={{ fontSize:"0.65rem", color:"rgba(255,255,255,0.3)", fontWeight:600 }}>per student</div>}
                </div>
              </div>
            </div>

            {/* Promo code input */}
            <div style={{ marginBottom:"1rem" }}>
              <div style={{ fontSize:"0.75rem", fontWeight:700, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>Promo Code</div>
              {appliedPromo ? (
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"rgba(34,197,94,0.1)", border:"1.5px solid rgba(34,197,94,0.35)", borderRadius:10, padding:"8px 14px" }}>
                  <span style={{ fontWeight:800, color:"#22C55E", fontSize:"0.88rem" }}>🎟️ {appliedPromo.code} — -${appliedPromo.discountAmount} off</span>
                  <button onClick={() => { setAppliedPromo(null); setPromoInput(""); setPromoError(""); }} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", fontSize:"1rem", lineHeight:1, padding:"0 0 0 8px" }}>✕</button>
                </div>
              ) : (
                <div style={{ display:"flex", gap:6 }}>
                  <input
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && applyPromoCode()}
                    placeholder="Enter code…"
                    style={{ flex:1, background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.14)", borderRadius:10, padding:"9px 14px", color:"#fff", fontFamily:"inherit", fontWeight:600, fontSize:"0.88rem", outline:"none" }}
                  />
                  <button
                    onClick={applyPromoCode}
                    disabled={promoChecking || !promoInput.trim()}
                    style={{ background: promoChecking || !promoInput.trim() ? "rgba(255,255,255,0.05)" : "rgba(155,92,246,0.2)", border:`1.5px solid ${promoChecking || !promoInput.trim() ? "rgba(255,255,255,0.1)" : "rgba(155,92,246,0.5)"}`, color: promoChecking || !promoInput.trim() ? "rgba(255,255,255,0.3)" : "#C084FC", borderRadius:10, padding:"9px 16px", cursor: promoChecking || !promoInput.trim() ? "default" : "pointer", fontFamily:"inherit", fontWeight:800, fontSize:"0.84rem", whiteSpace:"nowrap" }}>
                    {promoChecking ? "…" : "Apply"}
                  </button>
                </div>
              )}
              {promoError && (
                <div style={{ color:"#FF6B6B", fontSize:"0.78rem", fontWeight:700, marginTop:5 }}>{promoError}</div>
              )}
            </div>

            {payingPriceInfo.price === 0 ? (
              /* Promo covers 100% — no card needed */
              <div>
                {promoFreeError && (
                  <div style={{ background:"rgba(255,107,107,0.1)", border:"1px solid rgba(255,107,107,0.3)", borderRadius:10, padding:"0.7rem 1rem", fontSize:"0.83rem", fontWeight:700, color:"#FF6B6B", marginBottom:"0.8rem" }}>
                    {promoFreeError}
                  </div>
                )}
                <div style={{ display:"flex", gap:"0.7rem" }}>
                  <Btn onClick={() => { setEnrolled((prev) => prev.filter((e) => e.key !== payStep)); setPayStep(null); setAppliedPromo(null); setPromoInput(""); setPromoError(""); }} variant="ghost" style={{ flex:"0 0 auto" }} disabled={promoFreeEnrolling}>Cancel</Btn>
                  <Btn onClick={doPromoFreeEnroll} variant="success" style={{ flex:1 }} disabled={promoFreeEnrolling}>
                    {promoFreeEnrolling ? "Enrolling…" : "🚀 Confirm Free Enrollment"}
                  </Btn>
                </div>
              </div>
            ) : (
              /* Remaining balance — card required */
              <Elements stripe={stripePromise} options={{ appearance: { theme: "night" } }}>
                <PaymentForm
                  payingSessionData={payingSessionData}
                  payingEnrollment={payingEnrollment}
                  students={students}
                  payingPriceInfo={payingPriceInfo}
                  appliedPromo={appliedPromo}
                  parentInfo={parentInfo}
                  schoolName={schools.find((sc) => sc.id === payingSessionData?.school_id)?.name}
                  onSuccess={(row) => {
                    setEnrolled((prev) => prev.map((e) => e.key === payStep ? { ...e, dbId: row.id } : e));
                    setPaid((prev) => ({ ...prev, [payStep]: true }));
                    setPayStep(null);
                    setAppliedPromo(null);
                    setPromoInput("");
                    setActiveTab("schedule");
                  }}
                  onCancel={() => {
                    setEnrolled((prev) => prev.filter((e) => e.key !== payStep));
                    setPayStep(null);
                    setAppliedPromo(null);
                    setPromoInput("");
                    setPromoError("");
                  }}
                />
              </Elements>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
